import { useState, useEffect, useRef } from "react";
import { useAuth, api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, Mic, MicOff, FileText, Trash2, Save, 
  Printer, ClipboardList, Car, Users, CheckCircle2,
  RefreshCw, X
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

const ArbeitskartePage = () => {
  const [searchParams] = useSearchParams();
  const urlOrderId = searchParams.get("order");
  const [orders, setOrders] = useState([]);
  const [karten, setKarten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(urlOrderId || "");
  const [editingKarte, setEditingKarte] = useState(null);
  
  // Speech Recognition
  const [isListening, setIsListening] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const recognitionRef = useRef(null);
  
  // Form Data
  const [formData, setFormData] = useState({
    auftragsnummer: "",
    datum: new Date().toISOString().split('T')[0],
    kundenname: "",
    adresse: "",
    telefon_email: "",
    hersteller: "",
    fahrzeugtyp_modell: "",
    vin: "",
    baujahr: "",
    kennzeichen: "",
    kilometerstand: "",
    arbeiten: [],
    abschlusskontrolle: {
      alle_arbeiten_abgeschlossen: false,
      probefahrt_durchgefuehrt: false,
      funktionspruefung_elektrisch: false
    },
    bemerkungen: ""
  });

  const [newArbeit, setNewArbeit] = useState("");

  useEffect(() => {
    fetchData();
    initSpeechRecognition();
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Auto-load order data from URL parameter
  useEffect(() => {
    if (urlOrderId && orders.length > 0) {
      loadFromOrder(urlOrderId);
    }
  }, [urlOrderId, orders]);

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'de-DE';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        if (activeField) {
          handleVoiceInput(transcript);
        }
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error("Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen.");
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  };

  const startListening = (fieldName) => {
    if (!recognitionRef.current) {
      toast.error("Spracherkennung wird von diesem Browser nicht unterstützt");
      return;
    }
    setActiveField(fieldName);
    setIsListening(true);
    recognitionRef.current.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  const handleVoiceInput = (transcript) => {
    if (activeField === 'newArbeit') {
      setNewArbeit(transcript);
    } else if (activeField === 'bemerkungen') {
      setFormData(prev => ({
        ...prev,
        bemerkungen: prev.bemerkungen ? `${prev.bemerkungen} ${transcript}` : transcript
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [activeField]: transcript
      }));
    }
    setActiveField(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, kartenRes] = await Promise.all([
        api.get("/api/work-orders"),
        api.get("/api/arbeitskarten")
      ]);
      setOrders(ordersRes.data);
      setKarten(kartenRes.data);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const loadFromOrder = async (orderId) => {
    if (!orderId || orderId === "none") {
      resetForm();
      return;
    }
    try {
      const response = await api.get(`/api/arbeitskarten/prefill/${orderId}`);
      const data = response.data;
      // Also get the order itself for Abschlusskontrolle state
      const orderRes = await api.get(`/api/work-orders/${orderId}`);
      const orderData = orderRes.data;
      const abschluss = orderData?.arbeitskarte?.abschlusskontrolle || {};
      const bemerk = orderData?.arbeitskarte?.bemerkungen || data.description || "";

      setFormData(prev => ({
        ...prev,
        order_id: orderId,
        auftragsnummer: orderData?.auftragsnummer || "",
        kundenname: data.kundenname,
        adresse: data.adresse,
        telefon_email: data.telefon_email,
        hersteller: data.hersteller,
        fahrzeugtyp_modell: data.fahrzeugtyp_modell,
        vin: data.vin,
        baujahr: data.baujahr,
        kennzeichen: data.kennzeichen,
        kilometerstand: data.kilometerstand,
        arbeiten: data.arbeiten.map((a, i) => ({
          id: `task-${i}`,
          beschreibung: a.beschreibung,
          erledigt: a.erledigt,
          materialien: a.materialien || []
        })),
        abschlusskontrolle: {
          alle_arbeiten_abgeschlossen: abschluss.alle_arbeiten_abgeschlossen || false,
          probefahrt_durchgefuehrt: abschluss.probefahrt_durchgefuehrt || false,
          funktionspruefung_elektrisch: abschluss.funktionspruefung_elektrisch || false
        },
        bemerkungen: bemerk
      }));
      if (!urlOrderId) toast.success("Daten aus Auftrag geladen");
    } catch (err) {
      toast.error("Fehler beim Laden der Auftragsdaten");
    }
  };

  const addArbeit = () => {
    if (!newArbeit.trim()) return;
    setFormData(prev => ({
      ...prev,
      arbeiten: [...prev.arbeiten, { 
        id: `arbeit-${Date.now()}`, 
        beschreibung: newArbeit, 
        erledigt: false 
      }]
    }));
    setNewArbeit("");
  };

  const removeArbeit = (id) => {
    setFormData(prev => ({
      ...prev,
      arbeiten: prev.arbeiten.filter(a => a.id !== id)
    }));
  };

  const toggleArbeit = (id) => {
    setFormData(prev => ({
      ...prev,
      arbeiten: prev.arbeiten.map(a => 
        a.id === id ? { ...a, erledigt: !a.erledigt } : a
      )
    }));
  };

  const resetForm = () => {
    setFormData({
      auftragsnummer: "",
      datum: new Date().toISOString().split('T')[0],
      kundenname: "",
      adresse: "",
      telefon_email: "",
      hersteller: "",
      fahrzeugtyp_modell: "",
      vin: "",
      baujahr: "",
      kennzeichen: "",
      kilometerstand: "",
      arbeiten: [],
      abschlusskontrolle: {
        alle_arbeiten_abgeschlossen: false,
        probefahrt_durchgefuehrt: false,
        funktionspruefung_elektrisch: false
      },
      bemerkungen: ""
    });
    setSelectedOrderId("");
    setEditingKarte(null);
  };

  const handleSave = async () => {
    if (!formData.auftragsnummer.trim()) {
      toast.error("Bitte Auftragsnummer eingeben");
      return;
    }
    
    setSaving(true);
    try {
      const saveData = {
        ...formData,
        order_id: selectedOrderId || null
      };
      
      if (editingKarte) {
        await api.put(`/api/arbeitskarten/${editingKarte.id}`, saveData);
        toast.success("Arbeitskarte aktualisiert");
      } else {
        await api.post("/api/arbeitskarten", saveData);
        toast.success("Arbeitskarte gespeichert");
      }
      
      fetchData();
      resetForm();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const loadKarte = (karte) => {
    setEditingKarte(karte);
    setFormData({
      auftragsnummer: karte.auftragsnummer,
      datum: karte.datum,
      kundenname: karte.kundenname,
      adresse: karte.adresse,
      telefon_email: karte.telefon_email,
      hersteller: karte.hersteller,
      fahrzeugtyp_modell: karte.fahrzeugtyp_modell,
      vin: karte.vin,
      baujahr: karte.baujahr,
      kennzeichen: karte.kennzeichen,
      kilometerstand: karte.kilometerstand,
      arbeiten: karte.arbeiten || [],
      abschlusskontrolle: karte.abschlusskontrolle || {
        alle_arbeiten_abgeschlossen: false,
        probefahrt_durchgefuehrt: false,
        funktionspruefung_elektrisch: false
      },
      bemerkungen: karte.bemerkungen || ""
    });
    setSelectedOrderId(karte.order_id || "");
  };

  const deleteKarte = async (karteId) => {
    if (!window.confirm("Arbeitskarte löschen?")) return;
    try {
      await api.delete(`/api/arbeitskarten/${karteId}`);
      toast.success("Arbeitskarte gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const VoiceButton = ({ fieldName, className = "" }) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={`h-8 w-8 ${isListening && activeField === fieldName ? 'text-red-500 animate-pulse' : 'text-muted-foreground'} ${className}`}
      onClick={() => isListening ? stopListening() : startListening(fieldName)}
      data-testid={`voice-${fieldName}`}
    >
      {isListening && activeField === fieldName ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn print:space-y-2" data-testid="arbeitskarte-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Arbeitskarte
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Erstellen und verwalten Sie Arbeitskarten
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetForm} className="gap-2">
            <Plus className="w-4 h-4" />
            Neue Karte
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Drucken
          </Button>
        </div>
      </div>

      {/* Voice Status */}
      {isListening && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-3 print:hidden">
          <div className="animate-pulse">
            <Mic className="w-5 h-5 text-red-500" />
          </div>
          <span className="text-red-700 font-medium">Spracherkennung aktiv - Sprechen Sie jetzt...</span>
          <Button variant="ghost" size="sm" onClick={stopListening}>Stopp</Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Load from Order */}
          <Card className="print:hidden">
            <CardContent className="p-4">
              <Label className="text-sm font-medium">Aus vorhandenem Auftrag laden</Label>
              <Select value={selectedOrderId} onValueChange={(val) => { setSelectedOrderId(val); loadFromOrder(val); }}>
                <SelectTrigger className="mt-2" data-testid="select-order">
                  <SelectValue placeholder="Auftrag auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Keiner --</SelectItem>
                  {orders.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.description || "Auftrag"} - {o.vehicle_info} ({o.customer_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Main Form */}
          <Card className="border shadow-sm print:border-2 print:shadow-none">
            {/* Print Header */}
            <CardHeader className="hidden print:flex pb-2 border-b">
              <div className="text-center">
                <h1 className="text-xl font-bold">ARBEITSKARTE</h1>
                <p className="text-sm text-muted-foreground">VyWork</p>
              </div>
            </CardHeader>
            
            <CardContent className="p-4 lg:p-6 space-y-6">
              {/* Auftrag Section */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Auftragsnummer *</Label>
                  <div className="flex gap-1">
                    <Input
                      value={formData.auftragsnummer}
                      onChange={(e) => setFormData({...formData, auftragsnummer: e.target.value})}
                      placeholder="AB0001"
                      data-testid="input-auftragsnummer"
                    />
                    <VoiceButton fieldName="auftragsnummer" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={formData.datum}
                    onChange={(e) => setFormData({...formData, datum: e.target.value})}
                    data-testid="input-datum"
                  />
                </div>
              </div>

              {/* Kundendaten */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 border-b pb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Users className="w-4 h-4" />
                  Kundendaten
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Kundenname</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.kundenname}
                        onChange={(e) => setFormData({...formData, kundenname: e.target.value})}
                        placeholder="Firma / Name"
                        data-testid="input-kundenname"
                      />
                      <VoiceButton fieldName="kundenname" />
                    </div>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Adresse</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.adresse}
                        onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                        placeholder="Straße, PLZ Ort"
                        data-testid="input-adresse"
                      />
                      <VoiceButton fieldName="adresse" />
                    </div>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Telefon / E-Mail</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.telefon_email}
                        onChange={(e) => setFormData({...formData, telefon_email: e.target.value})}
                        placeholder="Kontaktdaten"
                        data-testid="input-telefon"
                      />
                      <VoiceButton fieldName="telefon_email" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Fahrzeugdaten */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 border-b pb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Car className="w-4 h-4" />
                  Fahrzeugdaten
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hersteller</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.hersteller}
                        onChange={(e) => setFormData({...formData, hersteller: e.target.value})}
                        placeholder="Mercedes-Benz"
                        data-testid="input-hersteller"
                      />
                      <VoiceButton fieldName="hersteller" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Fahrzeugtyp / Modell</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.fahrzeugtyp_modell}
                        onChange={(e) => setFormData({...formData, fahrzeugtyp_modell: e.target.value})}
                        placeholder="Sprinter 906"
                        data-testid="input-modell"
                      />
                      <VoiceButton fieldName="fahrzeugtyp_modell" />
                    </div>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>VIN (Fahrgestellnummer)</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.vin}
                        onChange={(e) => setFormData({...formData, vin: e.target.value.toUpperCase()})}
                        placeholder="WVWZZZ..."
                        data-testid="input-vin"
                      />
                      <VoiceButton fieldName="vin" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Baujahr</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.baujahr}
                        onChange={(e) => setFormData({...formData, baujahr: e.target.value})}
                        placeholder="2020"
                        data-testid="input-baujahr"
                      />
                      <VoiceButton fieldName="baujahr" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Kennzeichen</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.kennzeichen}
                        onChange={(e) => setFormData({...formData, kennzeichen: e.target.value.toUpperCase()})}
                        placeholder="B-AB 1234"
                        data-testid="input-kennzeichen"
                      />
                      <VoiceButton fieldName="kennzeichen" />
                    </div>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Kilometerstand</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.kilometerstand}
                        onChange={(e) => setFormData({...formData, kilometerstand: e.target.value})}
                        placeholder="125000"
                        data-testid="input-km"
                      />
                      <VoiceButton fieldName="kilometerstand" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Arbeiten */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 border-b pb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <ClipboardList className="w-4 h-4" />
                  Arbeiten
                </h3>
                
                {/* Add new work */}
                <div className="flex gap-2 print:hidden">
                  <div className="flex-1 flex gap-1">
                    <Input
                      value={newArbeit}
                      onChange={(e) => setNewArbeit(e.target.value)}
                      placeholder="Neue Arbeit hinzufügen..."
                      onKeyPress={(e) => e.key === 'Enter' && addArbeit()}
                      data-testid="input-new-arbeit"
                    />
                    <VoiceButton fieldName="newArbeit" />
                  </div>
                  <Button onClick={addArbeit} className="btn-primary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Work list */}
                <div className="space-y-2">
                  {formData.arbeiten.length > 0 ? (
                    formData.arbeiten.map((arbeit) => (
                      <div
                        key={arbeit.id}
                        className={`p-3 rounded-sm border ${
                          arbeit.erledigt ? 'bg-green-50 border-green-200' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={arbeit.erledigt}
                            onCheckedChange={() => toggleArbeit(arbeit.id)}
                          />
                          <span className={`flex-1 font-medium ${arbeit.erledigt ? 'line-through text-muted-foreground' : ''}`}>
                            {arbeit.beschreibung}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive print:hidden"
                            onClick={() => removeArbeit(arbeit.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {arbeit.materialien && arbeit.materialien.length > 0 && (
                          <div className="ml-9 mt-2 space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Material</p>
                            {arbeit.materialien.map((mat, mi) => (
                              <div key={mi} className="flex items-center gap-2 text-sm text-muted-foreground pl-2 border-l-2 border-muted">
                                <span className="font-medium text-foreground">{mat.bezeichnung || mat.beschreibung}</span>
                                {(mat.anzahl || mat.menge) && <span>| {mat.anzahl || mat.menge} {mat.einheit || ''}</span>}
                                {mat.artikelnummer && <span>| Art.Nr. {mat.artikelnummer}</span>}
                                {mat.kosten > 0 && <span>| {Number(mat.kosten).toFixed(2)} €</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Noch keine Arbeiten eingetragen
                    </p>
                  )}
                </div>
              </div>

              {/* Abschlusskontrolle */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 border-b pb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <CheckCircle2 className="w-4 h-4" />
                  Abschlusskontrolle
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={formData.abschlusskontrolle.alle_arbeiten_abgeschlossen}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        abschlusskontrolle: {...prev.abschlusskontrolle, alle_arbeiten_abgeschlossen: checked}
                      }))}
                    />
                    <Label>Alle Arbeiten abgeschlossen</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={formData.abschlusskontrolle.probefahrt_durchgefuehrt}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        abschlusskontrolle: {...prev.abschlusskontrolle, probefahrt_durchgefuehrt: checked}
                      }))}
                    />
                    <Label>Probefahrt durchgeführt</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={formData.abschlusskontrolle.funktionspruefung_elektrisch}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        abschlusskontrolle: {...prev.abschlusskontrolle, funktionspruefung_elektrisch: checked}
                      }))}
                    />
                    <Label>Funktionsprüfung elektrischer Komponenten</Label>
                  </div>
                </div>
              </div>

              {/* Bemerkungen */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Bemerkungen</Label>
                  <VoiceButton fieldName="bemerkungen" />
                </div>
                <Textarea
                  value={formData.bemerkungen}
                  onChange={(e) => setFormData({...formData, bemerkungen: e.target.value})}
                  placeholder="Zusätzliche Notizen..."
                  rows={3}
                  data-testid="input-bemerkungen"
                />
              </div>

              {/* Save Button */}
              <div className="flex gap-3 pt-4 print:hidden">
                <Button onClick={handleSave} disabled={saving} className="btn-primary flex-1 gap-2">
                  <Save className="w-4 h-4" />
                  {saving ? "Wird gespeichert..." : editingKarte ? "Aktualisieren" : "Speichern"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Saved Cards */}
        <div className="space-y-4 print:hidden">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                Gespeicherte Arbeitskarten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {karten.length > 0 ? (
                karten.map((karte) => (
                  <div
                    key={karte.id}
                    className={`p-3 rounded-sm border cursor-pointer hover:bg-muted/50 transition-colors ${
                      editingKarte?.id === karte.id ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => loadKarte(karte)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm">{karte.auftragsnummer}</p>
                        <p className="text-xs text-muted-foreground truncate">{karte.kundenname}</p>
                        <p className="text-xs text-muted-foreground">{karte.kennzeichen}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteKarte(karte.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Keine Arbeitskarten gespeichert
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ArbeitskartePage;
